import { Crosshair, Monitor, Music, Gem, Wrench } from 'lucide-react';

const categories = [
  {
    name: 'Firearms & Guns',
    slug: 'firearms',
    icon: Crosshair,
    description: 'Wide selection of handguns, rifles, shotguns, and ammunition. Buy, sell, or pawn your firearms with our licensed dealers. We offer fair appraisals and competitive prices on all firearms.',
    image: 'https://images.unsplash.com/photo-1689951732016-7a1ccd511ec9?w=600&q=80',
    keywords: 'firearms, guns, handguns, rifles, shotguns, ammunition, gun dealer, buy guns, sell guns, pawn firearms',
    span: 'md:col-span-7',
  },
  {
    name: 'Electronics',
    slug: 'electronics',
    icon: Monitor,
    description: 'Laptops, tablets, smartphones, gaming consoles, TVs, and more. Get top dollar for your used electronics or find incredible deals on quality pre-owned tech.',
    image: 'https://images.unsplash.com/photo-1749566679636-a9b0f4c52e08?w=600&q=80',
    keywords: 'electronics, laptops, tablets, smartphones, gaming consoles, pawn electronics, sell electronics',
    span: 'md:col-span-5',
  },
  {
    name: 'Musical Instruments',
    slug: 'musical-instruments',
    icon: Music,
    description: 'Guitars, amps, drums, keyboards, and more. Whether you are a beginner or a pro, we carry quality instruments at pawn shop prices. Trade in or pawn your gear.',
    image: 'https://images.unsplash.com/photo-1760413209618-f0be347670e6?w=600&q=80',
    keywords: 'musical instruments, guitars, amps, drums, keyboards, pawn instruments, sell guitar',
    span: 'md:col-span-4',
  },
  {
    name: 'Jewelry & Gold',
    slug: 'jewelry',
    icon: Gem,
    description: 'Gold, silver, diamonds, watches, and fine jewelry. We buy gold and offer honest appraisals. Get cash for your jewelry today or browse our stunning collection.',
    image: 'https://images.unsplash.com/photo-1605099776273-7704e15cd1e1?w=600&q=80',
    keywords: 'jewelry, gold, silver, diamonds, watches, cash for gold, sell jewelry, pawn jewelry, gold buyer',
    span: 'md:col-span-4',
  },
  {
    name: 'Power Tools',
    slug: 'power-tools',
    icon: Wrench,
    description: 'DeWalt, Milwaukee, Makita, and more. Quality power tools at unbeatable prices. Sell your used tools or pawn them when you need quick cash.',
    image: 'https://images.unsplash.com/photo-1770763233593-74dfd0da7bf0?w=600&q=80',
    keywords: 'power tools, DeWalt, Milwaukee, Makita, drill, saw, pawn tools, sell tools, used tools',
    span: 'md:col-span-4',
  },
];

export default function CategoriesSection() {
  return (
    <section
      id="categories"
      data-testid="categories-section"
      className="relative py-20 md:py-28 bg-background noise-overlay"
      aria-label="Product categories we buy, sell, and pawn"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14 md:mb-20">
          <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">Our Inventory</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading text-dirt-brown uppercase mb-5 chisel-heading">
            What We <span className="text-magma-red">Carry</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-2xl mx-auto mt-6">
            Cave City Pawn has something for everyone! From firearms to fine jewelry, 
            we buy, sell, and loan on quality items at the best prices in town.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {categories.map((cat, index) => {
            const Icon = cat.icon;
            return (
              <article
                key={cat.slug}
                data-testid={`category-card-${cat.slug}`}
                className={`${cat.span} reveal-child stone-card relative rounded-rock-lg overflow-hidden border-4 border-dirt-brown/20 min-h-[260px] md:min-h-[300px] group cursor-pointer`}
                aria-label={`${cat.name} - Buy, Sell, or Pawn`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="category-overlay absolute inset-0 transition-opacity duration-300 group-hover:opacity-90" />
                </div>

                {/* Content */}
                <div className="category-card-content relative z-10 h-full flex flex-col justify-end p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="category-icon w-11 h-11 bg-magma-red/90 rounded-full flex items-center justify-center shadow-soft">
                      <Icon size={20} className="text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading text-bone-white uppercase tracking-wide">
                      {cat.name}
                    </h3>
                  </div>
                  <p className="text-sm md:text-base text-stone-light/90 font-body leading-relaxed">
                    {cat.description}
                  </p>
                  <meta itemProp="keywords" content={cat.keywords} />
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA below categories */}
        <div className="text-center mt-10 md:mt-14">
          <a
            href="https://alabamapawnandstorage.com/pawn-shop"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="categories-shop-online-btn"
            className="cave-btn inline-flex items-center gap-3 px-8 py-4 bg-magma-red text-white font-heading text-base md:text-lg uppercase tracking-wider rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all duration-300"
          >
            Browse Our Full Inventory Online
          </a>
        </div>
      </div>
    </section>
  );
}
